import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  ChevronRight,
  PanelRightOpen,
  Loader2,
  MoreVertical,
  BookOpen,
  Printer,
  ChevronLeft,
  FileText,
  Save,
  CheckCircle2,
  X,
  Layers,
  AlertTriangle,
} from "lucide-react";
import api from "../../connector/api";

const CODE_ORDER = ["P", "L", "LE", "LU", "A", "AE", "AU"];

const DEFAULT_CODE_COLORS = {
  P: "#10b981",  // Emerald
  L: "#f59e0b",  // Amber
  LE: "#d97706", // Dark Amber
  LU: "#b45309", // Deep Amber
  A: "#ef4444",  // Red
  AE: "#dc2626", // Dark Red
  AU: "#b91c1c", // Deep Red
};

const getTodayFormatted = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/* ====================================================================
   BULK FILL ATTENDANCE MODAL COMPONENT
   ==================================================================== */
const FillAttendanceModal = ({
  isOpen,
  onClose,
  attendanceCodes = [],
  activeStudents = [],
  attendanceData = {},
  onApplyFill,
}) => {
  const [selectedCodeId, setSelectedCodeId] = useState(
    attendanceCodes[0]?.id || attendanceCodes[0]?.code || "P"
  );
  const [targetSlot, setTargetSlot] = useState("code1"); // 'code1' | 'code2'
  const [overrideStrategy, setOverrideStrategy] = useState("skip_existing"); // 'skip_existing' | 'override_all'

  // Sync initial selection when codes load
  useEffect(() => {
    if (attendanceCodes.length > 0 && !selectedCodeId) {
      setSelectedCodeId(attendanceCodes[0]?.id || attendanceCodes[0]?.code);
    }
  }, [attendanceCodes, selectedCodeId]);

  if (!isOpen) return null;

  // Calculate existing filled cells count for conflict preview
  const existingCount = activeStudents.filter((student) => {
    const studentId = student.id;
    return !!attendanceData[studentId]?.[targetSlot];
  }).length;

  const totalStudents = activeStudents.length;

  const handleApply = () => {
    onApplyFill({
      codeId: selectedCodeId,
      targetSlot,
      overrideStrategy,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-zinc-900/40 backdrop-blur-xs flex items-center justify-center p-4">
      <div className="relative w-full max-w-lg bg-white border border-zinc-200 rounded-lg shadow-xl overflow-hidden flex flex-col text-zinc-800 text-xs animate-in fade-in zoom-in-95 duration-150">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200 bg-zinc-50/80">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-teal-100/80 border border-teal-200 rounded-lg text-teal-700">
              <Layers className="w-4 h-4" />
            </div>
            <div>
              <h3 className="font-bold text-sm text-zinc-900">
                Bulk Fill Attendance
              </h3>
              <p className="text-zinc-500 text-[11px]">
                Apply an attendance code across all active students
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 transition-colors cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-5 space-y-5 overflow-y-auto custom-scroll max-h-[75vh]">
          {/* Step 1: Select Attendance Code */}
          <div className="space-y-2">
            <label className="block font-bold text-zinc-700 text-xs">
              1. Select Attendance Code
            </label>
            <div className="grid grid-cols-4 gap-2">
              {attendanceCodes.map((item) => {
                const codeValue = item.id || item.code;
                const isSelected = selectedCodeId === codeValue;
                const color = item.color || DEFAULT_CODE_COLORS[item.code] || "#10b981";

                return (
                  <button
                    key={codeValue}
                    type="button"
                    onClick={() => setSelectedCodeId(codeValue)}
                    style={{
                      borderColor: isSelected ? color : "#e4e4e7",
                      backgroundColor: isSelected ? `${color}15` : "#ffffff",
                    }}
                    className={`flex flex-col items-center justify-center p-2.5 rounded-lg border text-xs font-bold transition-all cursor-pointer ${
                      isSelected
                        ? "shadow-2xs ring-1"
                        : "hover:bg-zinc-50 hover:border-zinc-300"
                    }`}
                  >
                    <span
                      className="text-sm font-extrabold"
                      style={{ color: isSelected ? color : "#27272a" }}
                    >
                      {item.code}
                    </span>
                    <span className="text-[10px] text-zinc-500 font-normal truncate max-w-full">
                      {item.description || item.code}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Step 2: Target Column Selection */}
          <div className="space-y-2">
            <label className="block font-bold text-zinc-700 text-xs">
              2. Target Attendance Column
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label
                onClick={() => setTargetSlot("code1")}
                className={`flex items-center gap-2.5 p-3 rounded-lg border cursor-pointer transition-all ${
                  targetSlot === "code1"
                    ? "border-blue-500 bg-blue-50/50 text-blue-900 font-semibold shadow-2xs"
                    : "border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50"
                }`}
              >
                <input
                  type="radio"
                  name="targetSlot"
                  checked={targetSlot === "code1"}
                  onChange={() => setTargetSlot("code1")}
                  className="text-blue-600 focus:ring-blue-500"
                />
                <div>
                  <div className="font-bold text-xs">Primary (Code 1)</div>
                  <div className="text-[10px] text-zinc-500">
                    Main daily attendance
                  </div>
                </div>
              </label>

              <label
                onClick={() => setTargetSlot("code2")}
                className={`flex items-center gap-2.5 p-3 rounded-lg border cursor-pointer transition-all ${
                  targetSlot === "code2"
                    ? "border-blue-500 bg-blue-50/50 text-blue-900 font-semibold shadow-2xs"
                    : "border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50"
                }`}
              >
                <input
                  type="radio"
                  name="targetSlot"
                  checked={targetSlot === "code2"}
                  onChange={() => setTargetSlot("code2")}
                  className="text-blue-600 focus:ring-blue-500"
                />
                <div>
                  <div className="font-bold text-xs">Secondary (Code 2)</div>
                  <div className="text-[10px] text-zinc-500">
                    AM/PM or period tracking
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* Step 3: Conflict / Override Strategy */}
          <div className="space-y-2">
            <label className="block font-bold text-zinc-700 text-xs">
              3. Existing Records Strategy
            </label>

            {existingCount > 0 && (
              <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-[11px] flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                <span>
                  <strong>{existingCount}</strong> out of{" "}
                  <strong>{totalStudents}</strong> students already have a{" "}
                  {targetSlot === "code1" ? "Code 1" : "Code 2"} entry.
                </span>
              </div>
            )}

            <div className="space-y-2">
              <label
                className={`flex items-start gap-2.5 p-3 rounded-lg border cursor-pointer transition-all ${
                  overrideStrategy === "skip_existing"
                    ? "border-zinc-400 bg-zinc-50/80 font-medium"
                    : "border-zinc-200 bg-white hover:bg-zinc-50"
                }`}
              >
                <input
                  type="radio"
                  name="overrideStrategy"
                  checked={overrideStrategy === "skip_existing"}
                  onChange={() => setOverrideStrategy("skip_existing")}
                  className="mt-0.5 text-blue-600 focus:ring-blue-500"
                />
                <div>
                  <div className="font-semibold text-xs text-zinc-800">
                    Fill Empty Cells Only (Exclude existing)
                  </div>
                  <div className="text-[11px] text-zinc-500 leading-normal">
                    Preserves student entries that have already been recorded.
                  </div>
                </div>
              </label>

              <label
                className={`flex items-start gap-2.5 p-3 rounded-lg border cursor-pointer transition-all ${
                  overrideStrategy === "override_all"
                    ? "border-rose-400 bg-rose-50/40 font-medium"
                    : "border-zinc-200 bg-white hover:bg-zinc-50"
                }`}
              >
                <input
                  type="radio"
                  name="overrideStrategy"
                  checked={overrideStrategy === "override_all"}
                  onChange={() => setOverrideStrategy("override_all")}
                  className="mt-0.5 text-rose-600 focus:ring-rose-500"
                />
                <div>
                  <div className="font-semibold text-xs text-zinc-800">
                    Overwrite All Records
                  </div>
                  <div className="text-[11px] text-zinc-500 leading-normal">
                    Replaces all entries in {targetSlot === "code1" ? "Code 1" : "Code 2"} for all {totalStudents} students.
                  </div>
                </div>
              </label>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-zinc-200 bg-zinc-50/80 flex items-center justify-between">
          <span className="text-[11px] text-zinc-500">
            Target:{" "}
            <strong className="text-zinc-700">
              {totalStudents} Students
            </strong>
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 bg-white border border-zinc-200 hover:bg-zinc-100 text-zinc-700 rounded-lg font-semibold text-xs transition-colors shadow-2xs cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApply}
              className="px-4 py-1.5 bg-teal-800 hover:bg-teal-900 text-white rounded-lg font-semibold text-xs transition-colors shadow-2xs cursor-pointer flex items-center gap-1.5"
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              Apply Fill
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ====================================================================
   MAIN CLASSROOM TAB COMPONENT
   ==================================================================== */
const ClassroomTab = ({
  loading,
  error,
  activeClass,
  activeStudents = [],
  selectedSubjectId,
  currentUserId,
  teacherName = "Teacher",
  subjectName = "Subject",
  isRightSidebarOpen,
  setIsRightSidebarOpen,
}) => {
  const [selectedDate, setSelectedDate] = useState(getTodayFormatted);
  const [attendanceData, setAttendanceData] = useState({});
  // Track backend persisted state to compare against unsaved edits
  const [savedStatusMap, setSavedStatusMap] = useState({});
  
  const [attendanceCodes, setAttendanceCodes] = useState([]);
  const [loadingCodes, setLoadingCodes] = useState(true);
  
  // Controls which code field cell clicks apply to ("code1" or "code2")
  const [activeCodeTarget, setActiveCodeTarget] = useState("code1");

  // Modal State for Bulk Fill
  const [isFillModalOpen, setIsFillModalOpen] = useState(false);

  // Independent save states
  const [savingStatus, setSavingStatus] = useState(null); // Managed ONLY by Save All button
  const [autoSaving, setAutoSaving] = useState(false);   // Managed ONLY by cell auto-saves
  
  const [sessionId, setSessionId] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const showToast = useCallback((message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "success" });
    }, 3000);
  }, []);

  const [selectedCode, setSelectedCode] = useState("P");
  const saveTimersRef = useRef({});

  const handleDateChangeByDays = (days) => {
    setSelectedDate((prevDate) => {
      const [year, month, day] = prevDate.split("-").map(Number);
      const dateObj = new Date(year, month - 1, day);
      dateObj.setDate(dateObj.getDate() + days);

      const y = dateObj.getFullYear();
      const m = String(dateObj.getMonth() + 1).padStart(2, "0");
      const d = String(dateObj.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    });
  };

  // 1. Fetch Attendance Codes
  useEffect(() => {
    const fetchAttendanceCodes = async () => {
      try {
        const response = await api.get("settings/attendance/code/");
        const data = response.data.results || response.data;
        const rawList = Array.isArray(data) ? data : [];

        const sortedCodes = rawList.sort((a, b) => {
          const indexA = CODE_ORDER.indexOf(a.code);
          const indexB = CODE_ORDER.indexOf(b.code);

          if (indexA !== -1 && indexB !== -1) return indexA - indexB;
          if (indexA !== -1) return -1;
          if (indexB !== -1) return 1;
          return a.code.localeCompare(b.code);
        });

        setAttendanceCodes(sortedCodes);
        if (sortedCodes.length > 0) {
          setSelectedCode(sortedCodes[0].code);
        }
      } catch (err) {
        console.error("Failed to load attendance codes:", err);
      } finally {
        setLoadingCodes(false);
      }
    };

    fetchAttendanceCodes();
  }, []);

  // 2. Fetch Existing Attendance Session & Records
  useEffect(() => {
    if (!activeClass?.id || !selectedSubjectId || !selectedDate) return;

    const fetchExistingAttendance = async () => {
      try {
        const res = await api.get("teacher/attendance/sessions/", {
          params: {
            classroom: activeClass.id,
            subject_assignment: selectedSubjectId,
            date: selectedDate,
          },
        });

        const sessions = res.data.results || res.data;
        if (Array.isArray(sessions) && sessions.length > 0) {
          const currentSession = sessions[0];
          setSessionId(currentSession.id);

          const initialMap = {};
          if (Array.isArray(currentSession.records)) {
            currentSession.records.forEach((rec) => {
              initialMap[rec.student] = {
                code1: rec.attendance_code,
                comment1: rec.remarks || "",
                code2: rec.secondary_attendance_code || "",
                comment2: rec.secondary_remarks || "",
              };
            });
          }
          setAttendanceData(initialMap);
          // Mark fetched records as saved
          setSavedStatusMap(JSON.parse(JSON.stringify(initialMap)));
        } else {
          setSessionId(null);
          setAttendanceData({});
          setSavedStatusMap({});
        }
      } catch (err) {
        console.error("Error loading attendance session:", err);
      }
    };

    fetchExistingAttendance();
  }, [activeClass?.id, selectedSubjectId, selectedDate]);

  const getCodeBgColor = (codeVal) => {
    if (!codeVal) return null;
    const matched = attendanceCodes.find(
      (c) => c.id === codeVal || c.code === codeVal
    );
    if (matched?.color) return matched.color;
    const codeStr = matched ? matched.code : codeVal;
    return DEFAULT_CODE_COLORS[codeStr] || "#4b5563";
  };

  const getCodeId = (codeVal) => {
    if (!codeVal) return null;
    const matched = attendanceCodes.find(
      (c) => c.id === codeVal || c.code === codeVal
    );
    return matched ? matched.id : codeVal;
  };

  const getCodeDisplay = (codeVal) => {
    if (!codeVal) return "-";
    const matched = attendanceCodes.find(
      (c) => c.id === codeVal || c.code === codeVal
    );
    return matched ? matched.code : codeVal;
  };

  // Helper to check if student record (Code 1 & 2) matches backend saved state
  const isRecordSaved = (studentId, fieldPrefix = "code1") => {
    const current = attendanceData[studentId];
    const saved = savedStatusMap[studentId];

    const codeField = fieldPrefix;
    const commentField = fieldPrefix === "code1" ? "comment1" : "comment2";

    if (!current?.[codeField] && !saved?.[codeField]) return true;
    if (!current?.[codeField] || !saved?.[codeField]) return false;

    return (
      getCodeId(current[codeField]) === getCodeId(saved[codeField]) &&
      (current[commentField] || "") === (saved[commentField] || "")
    );
  };

  // 3. Auto-save Single Student Record
  const saveSingleRecord = async (studentId, studentRecord) => {
    const classId = typeof activeClass === "object" ? activeClass.id : activeClass;
    const subjectId = typeof selectedSubjectId === "object" ? selectedSubjectId.id : selectedSubjectId;
    const codeId1 = getCodeId(studentRecord.code1);
    const codeId2 = getCodeId(studentRecord.code2);

    if (!classId || !subjectId || !codeId1) return;

    setAutoSaving(true);
    try {
      const payload = {
        classroom: classId,
        subject_assignment: subjectId,
        date: selectedDate,
        records: [
          {
            student: studentId,
            attendance_code: codeId1,
            remarks: studentRecord.comment1 || "",
            secondary_attendance_code: codeId2 || null,
            secondary_remarks: studentRecord.comment2 || "",
          },
        ],
      };

      const response = sessionId
        ? await api.patch(`teacher/attendance/sessions/${sessionId}/`, payload)
        : await api.post("teacher/attendance/sessions/", payload);

      if (response.data?.id) {
        setSessionId(response.data.id);
      }

      // Update saved status map for this student
      setSavedStatusMap((prev) => ({
        ...prev,
        [studentId]: { ...studentRecord },
      }));
    } catch (err) {
      console.error("Auto-save failed:", err.response?.data || err);
    } finally {
      setAutoSaving(false);
    }
  };

  // 4. Handle Field Changes
  const handleAttendanceChange = (studentId, field, value) => {
    const updatedRecord = {
      ...attendanceData[studentId],
      [field]: value,
    };

    setAttendanceData((prev) => ({
      ...prev,
      [studentId]: updatedRecord,
    }));

    if (saveTimersRef.current[studentId]) {
      clearTimeout(saveTimersRef.current[studentId]);
    }

    saveTimersRef.current[studentId] = setTimeout(() => {
      saveSingleRecord(studentId, updatedRecord);
    }, 1000);
  };

  const handleCodeCellClick = (studentId, field = "code1") => {
    if (!selectedCode) return;
    const currentCode = attendanceData[studentId]?.[field];
    const codeIdToApply = getCodeId(selectedCode);

    const newCode = currentCode === codeIdToApply ? "" : codeIdToApply;
    handleAttendanceChange(studentId, field, newCode);
  };

  // 5. Bulk Save
  const handleBulkSave = async () => {
    if (!activeClass?.id || !selectedSubjectId) return;

    setSavingStatus("saving");

    const recordsPayload = Object.entries(attendanceData)
      .filter(([_, data]) => data?.code1)
      .map(([studentId, data]) => ({
        student: studentId,
        attendance_code: getCodeId(data.code1),
        remarks: data.comment1 || "",
        secondary_attendance_code: getCodeId(data.code2) || null,
        secondary_remarks: data.comment2 || "",
      }));

    if (recordsPayload.length === 0) {
      setSavingStatus(null);
      return;
    }

    const payload = {
      session_id: sessionId || null,
      classroom: activeClass.id,
      subject_assignment: selectedSubjectId,
      date: selectedDate,
      records: recordsPayload,
    };

    try {
      const response = await api.post("teacher/attendance/sessions/bulk-save/", payload);

      if (response.data?.id) {
        setSessionId(response.data.id);
      }

      // Mark all current attendance records as saved
      setSavedStatusMap(JSON.parse(JSON.stringify(attendanceData)));

      setSavingStatus("saved");
      showToast("Attendance saved successfully.", "success");
      setTimeout(() => setSavingStatus(null), 2500);
    } catch (err) {
      console.error("Bulk save failed:", err.response?.data || err.message);
      setSavingStatus("error");
      showToast("Failed to save attendance records.", "error");
    }
  };

  // Handle Bulk Fill execution from Modal
  const handleApplyBulkFill = ({ codeId, targetSlot, overrideStrategy }) => {
    const updated = { ...attendanceData };

    activeStudents.forEach((student, idx) => {
      const studentId = student.id || idx;
      const currentRecord = updated[studentId] || {};
      const existingValue = currentRecord[targetSlot];

      // Check strategy condition
      if (overrideStrategy === "override_all" || !existingValue) {
        updated[studentId] = {
          ...currentRecord,
          [targetSlot]: codeId,
        };
      }
    });

    setAttendanceData(updated);
    showToast(
      `Bulk fill applied to ${targetSlot === "code1" ? "Code 1" : "Code 2"}.`,
      "success"
    );
  };

  if (error) {
    return (
      <div className="p-12 text-center text-rose-500 text-xs font-medium">
        {error}
      </div>
    );
  }

  if (!activeClass) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8 text-center bg-zinc-50/50">
        {loading ? (
          <div className="flex flex-col items-center gap-2 text-zinc-500 font-medium">
            <Loader2 className="w-6 h-6 animate-spin text-zinc-800" />
            <span>Loading classroom workspace...</span>
          </div>
        ) : (
          <div className="max-w-md bg-white border border-zinc-200 rounded-xl p-8 shadow-xs flex flex-col items-center text-center space-y-3">
            <div className="w-12 h-12 rounded-full bg-zinc-100 text-zinc-800 flex items-center justify-center border border-zinc-200">
              <BookOpen className="w-6 h-6" />
            </div>
            <h2 className="text-xs font-bold text-zinc-900 uppercase tracking-wide">
              Please Select a Subject
            </h2>
            <p className="text-zinc-500 text-xs leading-relaxed">
              Choose a subject or classroom from the left sidebar menu to access
              student rosters and record daily attendance.
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex-1 p-4 space-y-3 w-full flex flex-col h-auto min-h-0 relative">
      {/* TOAST NOTIFICATION */}
      {toast.show && (
        <div
          className={`fixed bottom-5 right-5 z-50 flex items-center gap-2 px-4 py-2.5 rounded-lg shadow-lg text-xs font-semibold text-white transition-all transform duration-300 ${
            toast.type === "error" ? "bg-rose-600" : "bg-emerald-600"
          }`}
        >
          <span>{toast.message}</span>
          <button
            onClick={() => setToast((prev) => ({ ...prev, show: false }))}
            className="ml-2 hover:opacity-75 focus:outline-none cursor-pointer"
          >
            <X className="w-3.5 h-3.5 text-white" />
          </button>
        </div>
      )}

      {/* CONTROLS BAR */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 bg-white p-3 border border-zinc-200 rounded-lg shadow-xs shrink-0">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-1.5 flex-wrap">
            <div className="relative">
              <span className="absolute -top-1.5 left-2 bg-white px-1 text-[9px] text-zinc-400 font-medium">
                Day*
              </span>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="h-8 border border-zinc-300 rounded px-2 text-xs font-medium text-zinc-800 focus:outline-none focus:border-cyan-600 cursor-pointer"
              />
            </div>

            <button
              onClick={() => handleDateChangeByDays(-1)}
              className="h-8 px-2 border border-zinc-200 hover:bg-zinc-100 rounded text-zinc-600 transition-colors cursor-pointer"
              title="Previous Day"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => handleDateChangeByDays(1)}
              className="h-8 px-2 border border-zinc-200 hover:bg-zinc-100 rounded text-zinc-600 transition-colors cursor-pointer"
              title="Next Day"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>

            <button className="h-8 px-3 ml-2 border border-zinc-200 hover:bg-zinc-50 rounded text-sky-800 text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer">
              <Printer className="w-3.5 h-3.5 text-sky-700" />
              <span>Print</span>
            </button>

            <button className="h-8 px-3 border border-zinc-200 hover:bg-zinc-50 rounded text-emerald-800 text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer">
              <FileText className="w-3.5 h-3.5 text-emerald-700" />
              <span>Reports</span>
            </button>

            {/* FILL BUTTON -> OPENS MODAL */}
            <button
              onClick={() => setIsFillModalOpen(true)}
              className="h-8 px-4 bg-teal-800 hover:bg-teal-900 text-white rounded text-xs font-semibold transition-colors cursor-pointer flex items-center gap-1.5"
            >
              <Layers className="w-3.5 h-3.5" />
              <span>Fill</span>
            </button>

            <button
              onClick={handleBulkSave}
              disabled={savingStatus === "saving"}
              className="h-8 px-4 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer"
            >
              {savingStatus === "saving" ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Save className="w-3.5 h-3.5" />
              )}
              <span>Save All</span>
            </button>

            {savingStatus === "saved" && (
              <span className="text-emerald-600 text-xs flex items-center gap-1 font-medium ml-1">
                <CheckCircle2 className="w-3.5 h-3.5" /> Saved
              </span>
            )}
            {savingStatus === "error" && (
              <span className="text-rose-600 text-xs font-medium ml-1">
                Error saving
              </span>
            )}
            {autoSaving && (
              <span className="text-zinc-400 text-[10px] italic ml-1">
                Auto-saving...
              </span>
            )}
          </div>
        </div>

        {!isRightSidebarOpen && (
          <button
            onClick={() => setIsRightSidebarOpen(true)}
            className="h-8 px-3 border border-zinc-200 hover:bg-zinc-100 rounded text-zinc-700 transition-colors flex items-center gap-1.5 text-xs font-semibold shrink-0 cursor-pointer"
            title="Open Sidebar"
          >
            <PanelRightOpen className="w-4 h-4 text-zinc-600" />
            <span>Summary</span>
          </button>
        )}
      </div>

      {/* CLICKABLE STATUS LEGEND ROW & TARGET SLOT SWITCH */}
      <div className="bg-white border border-zinc-200 rounded-lg px-5 py-3 flex items-center justify-between gap-4 text-[13px] overflow-x-auto shrink-0 shadow-2xs">
        {/* Code Buttons */}
        <div className="flex items-center gap-2 overflow-x-auto">
          {loadingCodes ? (
            <span className="text-zinc-400 text-xs">Loading status codes...</span>
          ) : attendanceCodes.length > 0 ? (
            attendanceCodes.map((item) => {
              const isSelected = selectedCode === item.code;
              const bgColor = getCodeBgColor(item.code);

              return (
                <button
                  key={item.id || item.code}
                  onClick={() => setSelectedCode(item.code)}
                  style={{
                    backgroundColor: isSelected ? bgColor : "transparent",
                    color: isSelected ? "#ffffff" : "#27272a",
                    borderColor: isSelected ? bgColor : "#e4e4e7",
                  }}
                  className={`flex items-center gap-1.5 shrink-0 px-3 py-1 rounded border text-xs font-bold transition-all cursor-pointer ${
                    !isSelected && "hover:bg-zinc-100"
                  }`}
                  title={item.description}
                >
                  <span>{item.code}</span>
                </button>
              );
            })
          ) : (
            <span className="text-zinc-400 text-xs">
              No attendance codes found.
            </span>
          )}
        </div>
      </div>

      {/* TABLE CONTAINER */}
      <div className="bg-white border border-zinc-200 rounded-lg shadow-2xs overflow-hidden w-full flex flex-col flex-1 min-h-0">
        <div className="overflow-auto w-full max-h-[calc(100vh-220px)] custom-scroll">
          <table className="w-full min-w-[800px] text-left text-[13px] border-collapse">
            <thead className="bg-gray-100 border-b border-zinc-200 sticky top-0 z-10">
              <tr className="text-zinc-600 font-semibold border-b border-zinc-200">
                <th className="py-3 px-3 border-zinc-100 min-w-[220px] bg-gray-100">
                  <div className="flex items-center justify-between">
                    <span>Student Name</span>
                    <span className="text-[13px] text-blue-600 font-bold tracking-tighter">
                      A-Z
                    </span>
                  </div>
                </th>

                {/* Code 1 */}
                <th className="py-3 px-3 border-r border-zinc-100 w-24 bg-gray-100">
                  <div className="flex items-center justify-between">
                    <span>Code 1</span>
                    <MoreVertical className="w-3 h-3 text-zinc-400" />
                  </div>
                </th>

                {/* Comment 1 */}
                <th className="py-3 px-3 border-r border-zinc-100 min-w-[220px] bg-gray-100">
                  <div className="flex items-center justify-between">
                    <span>Comment 1</span>
                  </div>
                </th>

                {/* Code 2 */}
                <th className="py-3 px-3 border-l border-zinc-100 w-24 bg-gray-100">
                  <div className="flex items-center justify-between">
                    <span>Code 2</span>
                    <MoreVertical className="w-3 h-3 text-zinc-400" />
                  </div>
                </th>

                {/* Comment 2 */}
                <th className="py-3 px-3 min-w-[220px] bg-gray-100">
                  <div className="flex items-center justify-between">
                    <span>Comment 2</span>
                  </div>
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-zinc-200 text-zinc-800">
              {activeStudents.length > 0 ? (
                activeStudents.map((row, idx) => {
                  const studentId = row.id || idx;
                  const studentName =
                    row.name ||
                    `${row.last_name || ""}, ${row.first_name || ""}`.trim() ||
                    "N/A";

                  const currentRecord = attendanceData[studentId] || {};
                  const comment1 = currentRecord.comment1 || "";
                  const comment2 = currentRecord.comment2 || "";
                  
                  const codeDisplay1 = getCodeDisplay(currentRecord.code1);
                  const codeDisplay2 = getCodeDisplay(currentRecord.code2);

                  // Check if cells match persisted backend records
                  const saved1 = isRecordSaved(studentId, "code1");
                  const saved2 = isRecordSaved(studentId, "code2");

                  const cellTextColor1 = saved1
                    ? "#6b7280"
                    : getCodeBgColor(currentRecord.code1) || "#27272a";

                  const cellTextColor2 = saved2
                    ? "#6b7280"
                    : getCodeBgColor(currentRecord.code2) || "#27272a";

                  return (
                    <tr
                      key={studentId}
                      className="hover:bg-zinc-50/80 transition-colors border-b border-zinc-200"
                    >
                      {/* Student Name */}
                      <td className="py-2 px-3.5 border-r border-zinc-100 align-middle">
                        <div className="font-medium text-zinc-900 text-[12px] flex items-center justify-between gap-2">
                          <span>
                            {idx + 1}) {studentName}
                          </span>
                        </div>
                      </td>

                      {/* Code 1 Cell */}
                      <td
                        onClick={() => handleCodeCellClick(studentId, "code1")}
                        style={{
                          color: currentRecord.code1 ? cellTextColor1 : "inherit",
                        }}
                        className="py-2 px-3.5 border-zinc-100 text-center text-[12px] align-middle font-bold cursor-pointer hover:opacity-90 transition-all select-none"
                      >
                        <span className="inline-block w-full text-center">
                          {codeDisplay1}
                        </span>
                      </td>

                      {/* Comment 1 Input */}
                      <td className="py-2 px-3 border-r border-zinc-100 align-middle">
                        <input
                          type="text"
                          value={comment1}
                          onChange={(e) =>
                            handleAttendanceChange(
                              studentId,
                              "comment1",
                              e.target.value
                            )
                          }
                          placeholder=""
                          className="w-full h-8 border-none outline-none bg-transparent px-2 text-[11px] text-zinc-800 focus:outline-none focus:ring-0"
                        />
                      </td>

                      {/* Code 2 Cell */}
                      <td
                        onClick={() => handleCodeCellClick(studentId, "code2")}
                        style={{
                          color: currentRecord.code2 ? cellTextColor2 : "inherit",
                        }}
                        className="py-2 px-3.5 text-center text-[12px] align-middle font-bold cursor-pointer hover:opacity-90 transition-all select-none"
                      >
                        <span className="inline-block w-full text-center">
                          {codeDisplay2}
                        </span>
                      </td>

                      {/* Comment 2 Input */}
                      <td className="py-2 px-3 align-middle">
                        <input
                          type="text"
                          value={comment2}
                          onChange={(e) =>
                            handleAttendanceChange(
                              studentId,
                              "comment2",
                              e.target.value
                            )
                          }
                          placeholder=""
                          className="w-full h-8 border-none outline-none bg-transparent px-2 text-[11px] text-zinc-800 focus:outline-none focus:ring-0"
                        />
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan="5"
                    className="py-12 text-center text-zinc-400 text-xs"
                  >
                    {loading
                      ? "Loading attendance sheet..."
                      : "No student records found."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* BULK FILL ATTENDANCE MODAL */}
      <FillAttendanceModal
        isOpen={isFillModalOpen}
        onClose={() => setIsFillModalOpen(false)}
        attendanceCodes={attendanceCodes}
        activeStudents={activeStudents}
        attendanceData={attendanceData}
        onApplyFill={handleApplyBulkFill}
      />
    </div>
  );
};

export default ClassroomTab;